import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { useCardano } from "@cardano-foundation/cardano-connect-with-wallet";
import { NetworkType } from "@cardano-foundation/cardano-connect-with-wallet-core";
import { CARDANO_NETWORK } from "@constants/config";
import { withdrawFromFund } from "@/api/withdraw";
import { appendSignature } from "@/api/append-signature";
import { useBlockchainCooldown } from "@/hooks/useBlockchainCooldown";

// Helper to get Cardanoscan URL based on network
const getCardanoscanUrl = (txHash: string): string => {
  const baseUrl = CARDANO_NETWORK === NetworkType.MAINNET 
    ? "https://cardanoscan.io" 
    : "https://preprod.cardanoscan.io";
  return `${baseUrl}/transaction/${txHash}`;
};

// Helper to convert hex to bytes
const hexToBytes = (hex: string): Uint8Array => {
  const bytes = [];
  for (let i = 0; i < hex.length; i += 2) {
    bytes.push(parseInt(hex.substring(i, i + 2), 16));
  }
  return new Uint8Array(bytes);
};

export const PositionHoldsTableRow = ({ dataRow }: { dataRow: any }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isWithdrawing, setIsWithdrawing] = useState(false);
  const [withdrawMultiples, setWithdrawMultiples] = useState(1);
  const [showConfirm, setShowConfirm] = useState(false);
  
  const { enabledWallet } = useCardano({ limitNetwork: CARDANO_NETWORK });
  const { isOnCooldown, remainingSeconds, checkCooldown, startCooldown } = useBlockchainCooldown();

  // Check cooldown on mount
  useEffect(() => {
    checkCooldown();
    const interval = setInterval(checkCooldown, 1000);
    return () => clearInterval(interval);
  }, [checkCooldown]);

  if (!dataRow) return null;

  // Calculate multiples based on fund_factor
  const totalShares = Number(dataRow.total_amount);
  const fundFactor = Number(dataRow.fund_factor) || 420; // Default to 420 if not set
  const accruedRoyalty = Number(dataRow.accrued_royalty ?? 0);
  const totalMultiples = Math.floor(totalShares / fundFactor);
  
  // Calculate withdrawal amounts based on slider
  const withdrawShares = withdrawMultiples * fundFactor;
  const withdrawPercentage = totalShares > 0 ? (withdrawShares / totalShares) * 100 : 0;

  const handleWithdraw = async () => {
    if (!enabledWallet) {
      toast.error("Please connect your wallet first");
      return;
    }

    if (withdrawShares <= 0 || withdrawShares > totalShares) {
      toast.error("Invalid withdrawal amount");
      return;
    }

    // Check global cooldown period to prevent UTXO contention
    if (!checkCooldown()) {
      toast.error(`Please wait ${remainingSeconds} seconds before withdrawing. This ensures blockchain synchronization.`);
      return;
    }

    setIsWithdrawing(true);

    try {
      // Get wallet API
      if (!("cardano" in window)) {
        throw new Error("Cardano wallet not found");
      }
      const cardano = (window as any).cardano;
      const walletAPI = await cardano[enabledWallet].enable();
      
      // Get all wallet addresses and find which one has the fund tokens
      const usedAddresses = await walletAPI.getUsedAddresses();
      if (!usedAddresses || usedAddresses.length === 0) {
        throw new Error("No wallet addresses found");
      }
      
      // Convert all hex addresses to Bech32
      const CSL = await import("@emurgo/cardano-serialization-lib-asmjs");
      const bech32Addresses = usedAddresses.map((hexAddr: string) => {
        const addressBytes = hexToBytes(hexAddr);
        const address = CSL.Address.from_bytes(addressBytes);
        return address.to_bech32();
      });
      
      // Get all UTXOs from wallet to find which address has the fund tokens
      const allUtxos = await walletAPI.getUtxos();
      console.log("Total UTXOs in wallet:", allUtxos.length);
      
      // Find the address that has the fund tokens we want to withdraw
      // The fund token policy ID is the first 56 chars of the fund name (hex)
      const fundPolicyId = dataRow.name.substring(0, 56);
      console.log("Looking for fund tokens with policy:", fundPolicyId);
      
      let userAddress: string | null = null;
      
      for (const utxoHex of allUtxos) {
        const utxoBytes = hexToBytes(utxoHex);
        const utxo = CSL.TransactionUnspentOutput.from_bytes(utxoBytes);
        const output = utxo.output();
        const address = output.address().to_bech32();
        const amount = output.amount();
        
        const multiAsset = amount.multiasset();
        if (multiAsset) {
          const policies = multiAsset.keys();
          for (let i = 0; i < policies.len(); i++) {
            const policy = policies.get(i);
            if (policy.to_hex() === fundPolicyId) {
              userAddress = address;
              console.log(`Found fund tokens at address: ${address}`);
              break;
            }
          }
          if (userAddress) break;
        }
      }
      
      if (!userAddress) {
        // Fallback: use first address with any assets
        for (const utxoHex of allUtxos) {
          const utxoBytes = hexToBytes(utxoHex);
          const utxo = CSL.TransactionUnspentOutput.from_bytes(utxoBytes);
          const output = utxo.output();
          const address = output.address().to_bech32();
          const amount = output.amount();
          
          if (amount.multiasset() && amount.multiasset().len() > 0) {
            userAddress = address;
            console.log("Fallback: Using address with assets:", userAddress);
            break;
          }
        }
      }
      
      if (!userAddress) {
        userAddress = bech32Addresses[0];
        console.log("Fallback: Using first used address:", userAddress);
      }

      console.log("Requesting withdraw transaction...", {
        fundId: dataRow.name,
        amount: withdrawShares,
        address: userAddress,
      });

      // Step 1: Call backend to build unsigned transaction
      const unsignedTxCbor = await withdrawFromFund(
        dataRow.name,
        userAddress,
        withdrawShares
      );
      console.log("Received unsigned TX CBOR, length:", unsignedTxCbor.length);

      // Step 2: Sign with wallet
      console.log("Requesting wallet signature...");
      const witnessSetCbor = await walletAPI.signTx(unsignedTxCbor, true);
      console.log("Received witness set CBOR, length:", witnessSetCbor.length);

      // Step 3: Send to backend to append signature
      console.log("Sending witness set to backend...");
      const signedTxCbor = await appendSignature(unsignedTxCbor, witnessSetCbor);
      console.log("Received signed TX CBOR, length:", signedTxCbor.length);

      // Step 4: Submit transaction
      console.log("Submitting transaction...");
      const txHash = await walletAPI.submitTx(signedTxCbor);
      console.log("Withdrawal submitted! Hash:", txHash);

      // Show success toast with TX hash link to Cardanoscan
      const TxHashNotification = () => {
        const cardanoscanUrl = getCardanoscanUrl(txHash);
        
        return (
          <div>
            <div className="font-semibold mb-2">Withdrawal successful!</div>
            <a 
              href={cardanoscanUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-300 hover:text-blue-100 underline break-all"
              onClick={(e) => {
                // Also copy to clipboard when clicked
                navigator.clipboard.writeText(txHash);
                toast.info("TX hash copied to clipboard!", { autoClose: 2000 });
              }}
            >
              View on Cardanoscan: {txHash.substring(0, 16)}...
            </a>
          </div>
        );
      };
      
      toast.success(<TxHashNotification />, { 
        autoClose: 8000,
        onClick: () => {
          // Open cardanoscan in new tab when toast is clicked
          window.open(getCardanoscanUrl(txHash), '_blank');
        }
      });
      
      // Start global cooldown to prevent UTXO contention
      startCooldown();
      
      setShowConfirm(false);
      
      // Refresh page after short delay
      setTimeout(() => {
        window.location.reload();
      }, 2000);

    } catch (error) {
      console.error("Withdraw error:", error);
      toast.error(
        `Withdrawal failed: ${error instanceof Error ? error.message : "Unknown error"}`
      );
    } finally {
      setIsWithdrawing(false);
    }
  };

  return (
    <>
      <tr
        className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors border-b dark:border-gray-800"
        onClick={() => setIsOpen(!isOpen)}
      >
        <td className="py-6">
          <div className="flex items-center gap-3 ml-5">
            <span className="text-violet-600 font-bold w-4 text-center">{isOpen ? "▼" : "▶"}</span>
            <span className="text-gray-800 text-m md:text-xl font-bold dark:text-white uppercase tracking-tight">
              {dataRow.readable_name || "FUND"}
            </span>
          </div>
        </td>
        <td className="text-gray-800 text-m md:text-l font-medium text-right dark:text-white px-5">
          {totalShares.toLocaleString()} Shares
          <span className="text-xs text-gray-400 ml-2">
            ({totalMultiples} × {fundFactor})
          </span>
        </td>
        <td className="text-right px-10">
          <span className="text-xs text-violet-500 font-bold uppercase tracking-widest bg-violet-100 dark:bg-violet-900/20 px-3 py-1 rounded-full">
            {isOpen ? "Close" : "Assets"}
          </span>
        </td>
      </tr>

      {isOpen && (
        <tr className="bg-gray-50/50 dark:bg-gray-800/30">
          <td colSpan={3} className="p-8 border-b dark:border-gray-800">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
              <div>
                <h4 className="text-gray-400 uppercase text-[10px] font-black mb-4 tracking-[0.2em]">Portfolio Composition</h4>
                <div className="space-y-3">
                  {dataRow.decoded_tokens?.map((tokenName: string, idx: number) => (
                    <div key={idx} className="flex justify-between items-center bg-white dark:bg-gray-900 p-4 rounded-xl shadow-sm border border-gray-100 dark:border-gray-800">
                      <span className="font-bold text-gray-700 dark:text-gray-200">{tokenName}</span>
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] text-gray-400">TOKEN FACTOR</span>
                        <span className="text-sm font-mono text-violet-600 bg-violet-50 dark:bg-violet-900/40 px-2 py-0.5 rounded">
                          {dataRow.token_factors?.[idx]}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex flex-col gap-6">
                <div>
                  <h4 className="text-gray-400 uppercase text-[10px] font-black mb-4 tracking-[0.2em]">Economics</h4>
                  <div className="bg-white dark:bg-gray-900 p-6 rounded-xl shadow-sm border border-gray-100 dark:border-gray-800 space-y-4">
                    <div className="flex justify-between items-end">
                      <span className="text-sm text-gray-500">Accrued Royalties (shares)</span>
                      <span className="text-lg font-bold text-green-500">
                        {accruedRoyalty.toLocaleString()} <span className="text-xs">Shares</span>
                      </span>
                    </div>
                    <div className="flex justify-between items-end">
                      <span className="text-sm text-gray-500">Fund Global Factor</span>
                      <span className="text-lg font-bold dark:text-white">{fundFactor}</span>
                    </div>
                    <div className="flex justify-between items-end">
                      <span className="text-sm text-gray-500">Your Position</span>
                      <span className="text-lg font-bold dark:text-white">{totalMultiples} multiples</span>
                    </div>
                  </div>
                </div>

                {/* Withdraw Section */}
                {totalMultiples > 0 && (
                  <div className="bg-white dark:bg-gray-900 p-6 rounded-xl shadow-sm border border-gray-100 dark:border-gray-800">
                    <h4 className="text-gray-400 uppercase text-[10px] font-black mb-4 tracking-[0.2em]">Withdraw</h4>
                    
                    {!showConfirm ? (
                      <div className="space-y-4">
                        {/* Slider */}
                        <div>
                          <div className="flex justify-between mb-2">
                            <span className="text-sm text-gray-600 dark:text-gray-300">
                              Withdraw {withdrawMultiples} of {totalMultiples} multiples
                            </span>
                            <span className="text-sm font-bold text-violet-600">
                              {withdrawPercentage.toFixed(1)}%
                            </span>
                          </div>
                          <input
                            type="range"
                            min={1}
                            max={totalMultiples}
                            value={withdrawMultiples}
                            onChange={(e) => setWithdrawMultiples(Number(e.target.value))}
                            onClick={(e) => e.stopPropagation()}
                            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-700 accent-violet-600"
                          />
                          <div className="flex justify-between mt-1 text-xs text-gray-400">
                            <span>1</span>
                            <span>{Math.ceil(totalMultiples / 2)}</span>
                            <span>{totalMultiples} (All)</span>
                          </div>
                        </div>

                        {/* Withdrawal Preview */}
                        <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                          <p className="text-xs text-gray-500 mb-1">You will receive approximately:</p>
                          <p className="text-sm font-bold dark:text-white">
                            {withdrawShares.toLocaleString()} Shares worth of underlying tokens
                          </p>
                          <p className="text-xs text-gray-400 mt-1">
                            (ADA + proportional amount of each indexed token)
                          </p>
                        </div>

                        {/* Withdraw Button */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (!enabledWallet) {
                              toast.error("Please connect your wallet first");
                              return;
                            }
                            setShowConfirm(true);
                          }}
                          disabled={!enabledWallet || withdrawShares <= 0 || isOnCooldown}
                          className="w-full bg-violet-600 hover:bg-violet-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-semibold py-3 px-6 rounded-lg transition-colors"
                          title={isOnCooldown ? `Please wait ${remainingSeconds}s for blockchain confirmation` : undefined}
                        >
                          {!enabledWallet 
                            ? "Connect Wallet to Withdraw" 
                            : isOnCooldown 
                              ? `Wait ${remainingSeconds}s` 
                              : "Preview Withdrawal"}
                        </button>
                      </div>
                    ) : (
                      /* Confirmation Dialog */
                      <div className="space-y-4">
                        <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                          <h5 className="font-bold text-yellow-800 dark:text-yellow-200 mb-2">Confirm Withdrawal</h5>
                          <p className="text-sm text-yellow-700 dark:text-yellow-300">
                            You are about to withdraw <strong>{withdrawMultiples} multiples</strong> ({withdrawShares.toLocaleString()} shares) from this fund.
                          </p>
                          <p className="text-sm text-yellow-700 dark:text-yellow-300 mt-2">
                            This will burn your fund tokens and return the underlying assets (ADA + indexed tokens) to your wallet.
                          </p>
                        </div>

                        <div className="flex gap-3">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setShowConfirm(false);
                            }}
                            disabled={isWithdrawing}
                            className="flex-1 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-800 dark:text-white font-semibold py-3 px-6 rounded-lg transition-colors"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleWithdraw();
                            }}
                            disabled={isWithdrawing || isOnCooldown}
                            className="flex-1 bg-violet-600 hover:bg-violet-700 disabled:bg-gray-400 text-white font-semibold py-3 px-6 rounded-lg transition-colors flex items-center justify-center gap-2"
                          >
                            {isWithdrawing ? (
                              <>
                                <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-25" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                Processing...
                              </>
                            ) : isOnCooldown ? (
                              `Wait ${remainingSeconds}s`
                            ) : (
                              "Confirm Withdrawal"
                            )}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                <div className="mt-auto">
                  <div className="p-3 bg-gray-100 dark:bg-gray-800 rounded-lg">
                    <p className="text-[9px] text-gray-500 uppercase mb-1">Creator Security Hash</p>
                    <p className="text-[10px] font-mono text-gray-400 break-all">{dataRow.creator_pkh}</p>
                  </div>
                </div>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
};
